import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

// En un celular no entran tres columnas: se ve la bandeja O la conversación
// (con ← para volver) y el contexto del contacto se abre aparte. Reacciona al
// cambio de tamaño para que girar el teléfono no deje la pantalla a medias.
function useMovil(max = 760) {
  const [movil, setMovil] = useState(() => window.matchMedia(`(max-width: ${max}px)`).matches)
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${max}px)`)
    const h = (e) => setMovil(e.matches)
    mq.addEventListener('change', h)
    return () => mq.removeEventListener('change', h)
  }, [max])
  return movil
}
import { whatsappApi, CATEGORIAS, ESTADO_LABEL } from '../api/whatsapp'
import { colors, font, text, space, radius, shadow, sx } from '../design/tokens'
import { useStoredState } from '../hooks/useStoredState'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { fmtFecha, fmtFechaHora, fmtHoraDe, fmtMonto } from '../utils/fecha'
import Boton from '../components/Boton'
import Modal from '../components/Modal'

/**
 * Bandeja de WhatsApp — tres columnas: conversaciones · chat · contexto del cliente.
 * Diseño: docs/diseno-whatsapp-lgr.md §9 + docs/mockups/whatsapp-*.png.
 * Todo lo que se muestra sale de la base; lo que falta se muestra como faltante.
 */

// Las 4 vistas de TRABAJO van como chips; el archivo (resueltas/cerradas/spam)
// se agrupa detrás del chip "⋯" — propuesta aprobada 2 sep 2026.
const VISTAS_TRABAJO = [
  ['todas', 'Todas'], ['mias', 'Mías'], ['sin_asignar', 'Sin asignar'], ['esperando_cliente', 'Esperando'],
]
const VISTAS_ARCHIVO = [['resueltas', 'Resueltas'], ['cerradas', 'Cerradas'], ['spam', 'Spam']]
const VISTAS = [...VISTAS_TRABAJO, ...VISTAS_ARCHIVO]
const CAT_LABEL = Object.fromEntries(CATEGORIAS)
const DEFAULT_FILTROS = { vista: 'todas', categoria: '', asignado: '' }

const ESTADO_STYLE = {
  abierta: { bg: colors.warnBg, color: colors.warn },
  asignada: { bg: colors.linkBg, color: colors.link },
  esperando_cliente: { bg: colors.okBg, color: colors.ok },
  resuelta: { bg: colors.zonaBg, color: colors.zonaText },
  cerrada: { bg: colors.zonaBg, color: colors.zonaText },
  spam: { bg: colors.zonaBg, color: colors.zonaText },
  ia_atendiendo: { bg: colors.t80Bg, color: colors.t80Text },
  esperando_humano: { bg: colors.warnBg, color: colors.warn },
}

function iniciales(nombre) {
  if (!nombre) return '?'
  return nombre.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase()
}

function nombreDe(conv) {
  const c = conv?.contacto
  return c?.nombre_mostrar || null
}

function horaCorta(iso) {
  if (!iso) return ''
  const hoy = fmtFecha(new Date().toISOString())
  const f = fmtFecha(iso)
  return f === hoy ? fmtHoraDe(iso) : f.slice(0, 5)
}

/**
 * `enMovil`: montada dentro de la versión móvil de LGR (/m/whatsapp). Fuerza la
 * vista de una columna y se acomoda entre la cabecera y la barra de pestañas
 * del layout móvil en vez de taparlo todo (Martin, 27 sep 2026: "incorporá en
 * la versión mobile de LGR la parte de WA").
 */
export default function WhatsAppPage({ enMovil = false }) {
  const { user } = useAuth()
  const [filtros, setFiltros] = useStoredState('lgr.filtros.whatsapp', DEFAULT_FILTROS)
  const [q, setQ] = useState('')
  const [qDeb, setQDeb] = useState('')
  const [lista, setLista] = useState([])
  const [resumen, setResumen] = useState(null)
  const [estado, setEstado] = useState(null)
  const [apagada, setApagada] = useState(null)
  const [agentes, setAgentes] = useState([])
  const [equipos, setEquipos] = useState([])
  const [etiquetas, setEtiquetas] = useState([])
  const [lineas, setLineas] = useState([])
  // Escritorio: la selección vive en estado. App móvil: vive en la RUTA
  // (/m/whatsapp/:id), como lista→detalle en todas las secciones de /m, así la
  // flecha de la cabecera vuelve a la lista (Martin, 27 sep 2026: "la flecha
  // para ir para atrás no responde" — había dos flechas con destinos distintos).
  const [selIdEstado, setSelIdEstado] = useState(null)
  const paramsRuta = useParams()
  const navegar = useNavigate()
  const selId = enMovil ? (Number(paramsRuta.id) || null) : selIdEstado
  const setSelId = useCallback((id) => {
    if (enMovil) navegar(id ? `/m/whatsapp/${id}` : '/m/whatsapp')
    else setSelIdEstado(id)
  }, [enMovil, navegar])
  const [detalle, setDetalle] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => { const t = setTimeout(() => setQDeb(q.trim()), 300); return () => clearTimeout(t) }, [q])

  const cargarLista = useCallback(async () => {
    try {
      const params = { vista: filtros.vista, q: qDeb || undefined, categoria: filtros.categoria || undefined, asignado: filtros.asignado || undefined }
      const [l, r] = await Promise.all([whatsappApi.conversaciones(params), whatsappApi.resumen()])
      setLista(l.data); setResumen(r.data); setApagada(null)
    } catch (e) {
      if (e.response?.status === 503) setApagada(e.response.data?.message || 'La bandeja está apagada.')
    }
  }, [filtros, qDeb])

  const cargarDetalle = useCallback(async () => {
    // Sin selección, el detalle anterior se suelta: si quedaba cargado, la
    // pantalla del celular seguía mostrando el chat viejo y la flecha "no
    // respondía" (Martin, 27 sep 2026).
    if (!selId) { setDetalle(null); return }
    try {
      const r = await whatsappApi.conversacion(selId)
      setDetalle(r.data)
    } catch (e) {
      if (e.response?.status === 404) { setSelId(null); setDetalle(null) }
    }
  }, [selId])

  useEffect(() => { cargarLista() }, [cargarLista])
  useEffect(() => { cargarDetalle() }, [cargarDetalle])
  useAutoRefresh(cargarLista, 15000)
  useAutoRefresh(cargarDetalle, 10000, !!selId)

  useEffect(() => {
    whatsappApi.estado().then((r) => setEstado(r.data)).catch(() => {})
    Promise.all([whatsappApi.agentes(), whatsappApi.equipos(), whatsappApi.etiquetas(), whatsappApi.lineas()])
      .then(([a, e, t, l]) => { setAgentes(a.data); setEquipos(e.data); setEtiquetas(t.data); setLineas(l.data) })
      .catch(() => {})
  }, [])

  const refrescar = useCallback(async () => { await Promise.all([cargarLista(), cargarDetalle()]) }, [cargarLista, cargarDetalle])

  // Acción sobre la conversación seleccionada: corre, refresca, muestra el error del backend si lo hay.
  const accion = useCallback(async (fn) => {
    setError('')
    try { await fn(); await refrescar() } catch (e) {
      const d = e.response?.data
      setError(d?.errors ? Object.values(d.errors).flat().join(' ') : (d?.message || 'No se pudo completar la acción.'))
    }
  }, [refrescar])

  const grupos = useMemo(() => {
    const orden = ['sin', ...CATEGORIAS.map(([k]) => k)]
    const map = {}
    for (const c of lista) { const k = c.categoria || 'sin'; (map[k] ||= []).push(c) }
    return orden.filter((k) => map[k]?.length).map((k) => ({ key: k, label: k === 'sin' ? 'Sin categoría' : CAT_LABEL[k], items: map[k] }))
  }, [lista])

  const conv = detalle?.conversacion
  const movilAncho = useMovil()
  const movil = enMovil || movilAncho
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false)
  const hayFiltros = !!(filtros.categoria || filtros.asignado)
  const [verContexto, setVerContexto] = useState(false)
  useEffect(() => { setVerContexto(false) }, [selId])
  const verLista = !movil || !conv
  const verChat = !movil || (conv && !verContexto)
  const verDerecha = !movil || (conv && verContexto)

  return (
    <div style={{ ...st.page, ...(movil ? (enMovil ? st.pageMovilApp : st.pageMovil) : {}) }}>
      {/* Cabecera de UNA línea (propuesta aprobada 2 sep 2026): título +
          línea/número en mono + stats en texto plano. Los contadores de
          vistas viven en los chips; acá solo lo que pide acción o mide el día. */}
      {/* Dentro de la app móvil el título ya está en la cabecera del layout: no se repite. */}
      {!enMovil && <div style={st.head}>
        <h2 style={st.title}>{movil && <Link to="/" style={st.salir} title="Volver a LGR">← LGR</Link>}WhatsApp</h2>
        {!movil && (
          <span style={{ fontSize: text.sec, color: colors.textSec, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {lineas.filter((l) => l.activa).length
              ? lineas.filter((l) => l.activa).map((l) => (
                <span key={l.id}>{l.nombre}{l.numero_visible ? <> · <span style={sx.mono}>{l.numero_visible}</span></> : null}</span>
              )).reduce((a, b) => [a, '  ·  ', b])
              : 'Sin líneas conectadas'}
          </span>
        )}
        {resumen?.kpis && !movil && (
          <div style={st.stats}>
            <Stat k="Sin asignar" v={resumen.kpis.sin_asignar} warn={resumen.kpis.sin_asignar > 0} />
            <Stat k="Sin leer" v={resumen.kpis.no_leidos} />
            <span style={st.stat}>
              Hoy <b style={st.statV}>{resumen.kpis.entrantes_hoy}</b> <span style={{ color: colors.textMeta }}>entraron</span>
              {' '}<b style={st.statV}>{resumen.kpis.respondidos_hoy}</b> <span style={{ color: colors.textMeta }}>respondidos</span>
            </span>
          </div>
        )}
      </div>}

      {apagada && <div style={st.bannerWarn}>⏸ {apagada} Se enciende con <span style={sx.mono}>WHATSAPP_INBOX_ENABLED=true</span> en el servidor.</div>}
      {estado && !apagada && (estado.fake || !estado.outbound || !estado.app_secret) && (
        <div style={st.bannerWarn}>
          {estado.fake && <span>🧪 Modo simulación (<span style={sx.mono}>WHATSAPP_FAKE</span>): nada sale a WhatsApp de verdad. </span>}
          {!estado.outbound && <span>⏸ Envíos apagados (<span style={sx.mono}>WHATSAPP_OUTBOUND_ENABLED</span>): se puede leer y anotar, no responder. </span>}
          {!estado.app_secret && <span>🔐 Falta <span style={sx.mono}>WHATSAPP_APP_SECRET</span>: el webhook rechaza todo lo que manda Meta. </span>}
        </div>
      )}

      <div style={{ ...st.shell, ...(enMovil ? st.shellMovil : {}) }}>
        {/* ── Bandeja ── */}
        {verLista && <div style={{ ...st.left, ...(movil ? st.colMovil : {}) }}>
          <input style={st.search} placeholder="🔍 Buscar nombre, teléfono, texto…" value={q} onChange={(e) => setQ(e.target.value)} />
          <div style={st.chips}>
            {VISTAS_TRABAJO.map(([k, label]) => (
              <button key={k} type="button" onClick={() => setFiltros({ ...filtros, vista: k })} style={{ ...st.chip, ...(filtros.vista === k ? st.chipOn : {}) }}>
                {label}{(resumen?.vistas?.[k] ?? 0) > 0 && <b style={{ ...st.chipN, ...(filtros.vista === k ? { color: colors.link } : {}) }}>{resumen.vistas[k]}</b>}
              </button>
            ))}
            <ChipArchivo filtros={filtros} setFiltros={setFiltros} resumen={resumen} />
          </div>
          <div style={{ ...st.filtroRow, ...(filtrosAbiertos ? { borderBottom: 'none', paddingBottom: 4 } : {}) }}>
            <button type="button" style={{ ...st.chip, ...(filtrosAbiertos || hayFiltros ? st.chipOn : {}) }} onClick={() => setFiltrosAbiertos((v) => !v)}>
              Filtros{hayFiltros ? <b style={{ ...st.chipN, color: colors.link }}>{(filtros.categoria ? 1 : 0) + (filtros.asignado ? 1 : 0)}</b> : ''}
            </button>
            {!filtrosAbiertos && filtros.categoria && (
              <button type="button" style={st.filtroActivo} onClick={() => setFiltros({ ...filtros, categoria: '' })}>
                {filtros.categoria === 'sin' ? 'Sin categoría' : CAT_LABEL[filtros.categoria]} ✕
              </button>
            )}
            {!filtrosAbiertos && filtros.asignado && (
              <button type="button" style={st.filtroActivo} onClick={() => setFiltros({ ...filtros, asignado: '' })}>
                {agentes.find((a) => String(a.id) === String(filtros.asignado))?.nombre || 'Persona'} ✕
              </button>
            )}
          </div>
          {filtrosAbiertos && (
            <div style={st.filtroRow}>
              <select style={st.selectSm} value={filtros.categoria} onChange={(e) => setFiltros({ ...filtros, categoria: e.target.value })}>
                <option value="">Toda categoría</option>
                <option value="sin">Sin categoría</option>
                {CATEGORIAS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <select style={st.selectSm} value={filtros.asignado} onChange={(e) => setFiltros({ ...filtros, asignado: e.target.value })}>
                <option value="">Cualquier persona</option>
                {agentes.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
              </select>
            </div>
          )}
          <div style={st.listaScroll}>
            {lista.length === 0 && <div style={st.vacio}>{apagada ? '' : 'No hay conversaciones en esta vista.'}</div>}
            {grupos.map((g) => (
              <div key={g.key}>
                <div style={st.grupo}>{g.label}</div>
                {g.items.map((c) => <Fila key={c.id} c={c} sel={c.id === selId} onClick={() => { setSelId(c.id); setError('') }} />)}
              </div>
            ))}
          </div>
        </div>}

        {/* ── Chat ── */}
        {verChat && <div style={st.center}>
          {!conv ? (
            <div style={st.vacioCentro}>Elegí una conversación de la izquierda.</div>
          ) : (
            <Chat
              detalle={detalle} yo={user} agentes={agentes} equipos={equipos} estado={estado}
              error={error} setError={setError} accion={accion} refrescar={refrescar}
              movil={movil} enMovilApp={enMovil} onVolver={() => setSelId(null)} onContexto={() => setVerContexto(true)}
            />
          )}
        </div>}

        {/* ── Contexto ── */}
        {verDerecha && <div style={{ ...st.right, ...(movil ? st.colMovil : {}) }}>
          {movil && <div style={st.volverBar}><Boton sm onClick={() => setVerContexto(false)}>← Volver al chat</Boton></div>}
          {conv && <Contexto detalle={detalle} etiquetas={etiquetas} accion={accion} />}
        </div>}
      </div>
    </div>
  )
}

function Stat({ k, v, warn }) {
  return (
    <span style={st.stat}>
      {warn && <span style={st.statDot} />}{k} <b style={{ ...st.statV, ...(warn ? { color: colors.warnStrong } : {}) }}>{v}</b>
    </span>
  )
}

/** El archivo de la bandeja (Resueltas · Cerradas · Spam) detrás del chip "⋯". */
function ChipArchivo({ filtros, setFiltros, resumen }) {
  const [abierto, setAbierto] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!abierto) return undefined
    const fuera = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false) }
    document.addEventListener('mousedown', fuera)
    return () => document.removeEventListener('mousedown', fuera)
  }, [abierto])

  const activa = VISTAS_ARCHIVO.find(([k]) => k === filtros.vista)
  return (
    <div ref={ref} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <button
        type="button" title="Resueltas · Cerradas · Spam"
        onClick={() => setAbierto((v) => !v)}
        style={{ ...st.chip, ...(activa ? st.chipOn : {}) }}
      >{activa ? activa[1] : '…'}</button>
      {abierto && (
        <div style={st.archivoPop}>
          {VISTAS_ARCHIVO.map(([k, label]) => (
            <button
              key={k} type="button" style={st.archivoItem}
              onClick={() => { setFiltros({ ...filtros, vista: k }); setAbierto(false) }}
            >
              {label}{(resumen?.vistas?.[k] ?? 0) > 0 && <b style={st.chipN}>{resumen.vistas[k]}</b>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Fila de la bandeja: nombre + hora arriba y los DOS últimos renglones de la
 * conversación (Martin, 2 sep 2026 — el ida y vuelta, con "Vos:" en lo que
 * salió de Ligier). UNA sola señal, la más urgente (no leídos > sin asignar).
 */
function Fila({ c, sel, onClick }) {
  const nombre = nombreDe(c)
  const señal = c.no_leidos > 0
    ? <span style={st.noLeidos}>{c.no_leidos}</span>
    : (!c.asignado && ['abierta', 'esperando_humano'].includes(c.estado)
      ? <span style={st.sinAsignar}>Sin asignar</span>
      : null)
  const renglones = c.renglones?.length ? c.renglones : [c.ultimo_texto || '—']

  return (
    <div onClick={onClick} style={{ ...st.fila, ...(sel ? st.filaSel : {}) }} title={c.contacto?.telefono_formateado}>
      <div style={st.filaTop}>
        <span style={{ ...st.filaNombre, ...(nombre ? {} : { ...sx.mono, fontWeight: 500, color: colors.textSec }) }}>{nombre || c.contacto?.telefono_formateado}</span>
        <span style={{ ...st.filaHora, ...sx.mono }}>{horaCorta(c.ultimo_mensaje_at)}</span>
      </div>
      {renglones.slice(0, -1).map((r, i) => <span key={i} style={st.filaPreview}>{r}</span>)}
      <div style={st.filaTop}>
        <span style={st.filaPreview}>{renglones[renglones.length - 1]}</span>
        {señal}
      </div>
    </div>
  )
}

function Badge({ bg = colors.zonaBg, color = colors.zonaText, children, style }) {
  return <span style={{ ...sx.badge, background: bg, color, ...style }}>{children}</span>
}

// ─── Chat ───────────────────────────────────────────────────────────────

function Chat({ detalle, yo, agentes, equipos, estado, error, setError, accion, refrescar, movil = false, enMovilApp = false, onVolver, onContexto }) {
  const conv = detalle.conversacion
  const mensajes = detalle.mensajes || []
  const [modo, setModo] = useState('responder')
  const [texto, setTexto] = useState('')
  const [archivo, setArchivo] = useState(null)
  const archivoRef = useRef(null)
  const [enviando, setEnviando] = useState(false)
  const [modalAsignar, setModalAsignar] = useState(false)
  const [modalNombre, setModalNombre] = useState(false)
  const [reenviarMsg, setReenviarMsg] = useState(null)
  const [respondeA, setRespondeA] = useState(null)
  const [verEventos, setVerEventos] = useState(false)
  // Celular: la cabecera se compacta (nombre + estado + "⋯"); las acciones y los
  // selectores se abren a pedido, así los mensajes tienen la pantalla (Martin,
  // 27 sep 2026: "los mensajes están fuera de pantalla y no se ven completos").
  const [accionesAbiertas, setAccionesAbiertas] = useState(false)
  const scrollRef = useRef(null)
  const ultimoIdRef = useRef(null)
  const textareaRef = useRef(null)
  const [emojis, setEmojis] = useState(false)

  // Inserta el emoji donde está el cursor (no al final) y devuelve el foco al cuadro.
  const insertarEmoji = (emoji) => {
    const el = textareaRef.current
    const ini = el ? el.selectionStart : texto.length
    const fin = el ? el.selectionEnd : texto.length
    const nuevo = texto.slice(0, ini) + emoji + texto.slice(fin)
    setTexto(nuevo)
    setEmojis(false)
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(ini + emoji.length, ini + emoji.length) } })
  }

  // Al cambiar de conversación o llegar mensajes nuevos, ir al final.
  useEffect(() => {
    const ultimo = mensajes[mensajes.length - 1]?.id
    if (ultimo !== ultimoIdRef.current) {
      ultimoIdRef.current = ultimo
      requestAnimationFrame(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight })
    }
  }, [mensajes, conv.id])

  useEffect(() => { setTexto(''); setArchivo(null); setRespondeA(null); setModo('responder'); setEmojis(false); setAccionesAbiertas(false) }, [conv.id])

  const enviar = async () => {
    const conAdjunto = modo === 'responder' && archivo
    if ((!texto.trim() && !conAdjunto) || enviando) return
    setEnviando(true); setError('')
    try {
      if (modo === 'nota') await whatsappApi.nota(conv.id, texto)
      else if (conAdjunto) await whatsappApi.adjunto(conv.id, archivo, texto)
      else await whatsappApi.responder(conv.id, texto, respondeA?.id)
      setTexto(''); setArchivo(null); setRespondeA(null)
      await refrescar()
    } catch (e) {
      const d = e.response?.data
      setError(d?.errors ? Object.values(d.errors).flat().join(' ') : (d?.message || 'No se pudo enviar.'))
    } finally { setEnviando(false) }
  }

  // Enter manda (como WhatsApp); Shift+Enter baja un renglón. ⌘/Ctrl+Enter sigue valiendo.
  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing) { e.preventDefault(); enviar() }
  }

  const nombre = nombreDe(conv)
  const esMia = conv.asignado_usuario_id === yo?.id
  const cerrada = ['cerrada', 'resuelta'].includes(conv.estado)
  const spam = conv.estado === 'spam'
  const puedeResponder = !!estado?.outbound && conv.ventana_abierta && !spam

  return (
    <>
      <div style={{ ...st.chatHead, ...(movil ? st.chatHeadMovil : {}) }}>
        {movil && !enMovilApp && <button type="button" style={st.volver} onClick={onVolver} title="Volver a la bandeja">←</button>}
        <div style={st.avatar}>{nombre ? iniciales(nombre) : '?'}</div>
        <div style={movil ? { minWidth: 0, flex: 1 } : { minWidth: 0 }}>
          <div style={{ ...st.chatNombre, ...(movil ? { overflow: 'hidden', textOverflow: 'ellipsis' } : {}) }}>
            <span style={movil ? { overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>{nombre || <span style={sx.mono}>{conv.contacto?.telefono_formateado}</span>}</span>
            <button type="button" style={st.lapiz} title="Editar nombre del contacto" onClick={() => setModalNombre(true)}>✎</button>
          </div>
          {/* Celular: solo el teléfono en una línea (código de cliente y nombre de perfil, con "⋯"). */}
          <div style={{ ...st.chatSub, ...sx.mono, ...(movil ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } : {}) }}>
            {(!movil || accionesAbiertas) && conv.contacto?.cliente?.codigo_presea ? `${conv.contacto.cliente.codigo_presea} · ` : ''}{conv.contacto?.telefono_formateado}
            {(!movil || accionesAbiertas) && conv.contacto?.nombre_perfil && nombre !== conv.contacto.nombre_perfil ? <span style={{ fontFamily: font.sans }}> · en WhatsApp: {conv.contacto.nombre_perfil}</span> : null}
          </div>
        </div>
        {(!movil || accionesAbiertas) && (
          <div style={movil ? st.selectsMovil : { display: 'contents' }}>
            <select style={{ ...st.selectInline, ...(movil ? st.selectMovil : {}) }} value={conv.categoria || ''} onChange={(e) => accion(() => whatsappApi.actualizar(conv.id, { categoria: e.target.value || null }))} title="Categoría">
              <option value="">Sin categoría</option>
              {CATEGORIAS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <select style={{ ...st.selectInline, ...(movil ? st.selectMovil : {}) }} value={conv.prioridad} onChange={(e) => accion(() => whatsappApi.actualizar(conv.id, { prioridad: e.target.value }))} title="Prioridad">
              <option value="baja">Prioridad baja</option>
              <option value="normal">Prioridad normal</option>
              <option value="alta">Prioridad alta</option>
            </select>
          </div>
        )}
        {(!movil || accionesAbiertas) && (conv.asignado
          ? <Badge bg={colors.linkBg} color={colors.link}>{esMia ? 'Asignada a mí' : `Asignada a ${conv.asignado.nombre}`}</Badge>
          : <Badge bg={ESTADO_STYLE[conv.estado]?.bg} color={ESTADO_STYLE[conv.estado]?.color}>{ESTADO_LABEL[conv.estado]}</Badge>)}
        {conv.equipo && (!movil || accionesAbiertas) && <Badge>{conv.equipo.nombre}</Badge>}
        {!movil && <div style={{ flex: 1 }} />}
        {movil && (
          <button type="button" style={{ ...st.puntos, ...(accionesAbiertas ? { color: colors.link } : {}) }} onClick={() => setAccionesAbiertas((v) => !v)} aria-label="Acciones de la conversación" aria-expanded={accionesAbiertas} title="Acciones">⋯</button>
        )}
        {(!movil || accionesAbiertas) && (
          <div style={movil ? st.accionesMovil : { display: 'contents' }}>
            {movil && <Boton sm={!movil} onClick={onContexto}>ⓘ Contacto</Boton>}
            {!spam && !esMia && !cerrada && <Boton sm={!movil} onClick={() => accion(() => whatsappApi.tomar(conv.id))}>Tomar</Boton>}
            {!spam && <Boton sm={!movil} onClick={() => setModalAsignar(true)}>{conv.asignado ? '⇄ Transferir' : 'Asignar'}</Boton>}
            {!spam && !cerrada && <Boton sm={!movil} variante="success" onClick={() => accion(() => whatsappApi.resolver(conv.id))}>✓ Resuelta</Boton>}
            {!spam && !cerrada && <Boton sm={!movil} variante="destructive" onClick={() => accion(() => whatsappApi.cerrar(conv.id))}>Cerrar</Boton>}
            {cerrada && <Boton sm={!movil} onClick={() => accion(() => whatsappApi.reabrir(conv.id))}>Reabrir</Boton>}
            {spam ? <Boton sm={!movil} onClick={() => accion(() => whatsappApi.noSpam(conv.id))}>Quitar de spam</Boton> : <Boton sm={!movil} variante="secondary" onClick={() => accion(() => whatsappApi.spam(conv.id))}>Spam</Boton>}
          </div>
        )}
      </div>

      {conv.contacto?.cliente_ambiguo && !conv.contacto?.cliente && (
        <div style={st.bannerWarnSlim}>⚠ Este teléfono figura en más de una ficha de cliente. Elegí cuál es en el panel de la derecha — LGR no elige por vos.</div>
      )}

      <div style={{ ...st.mensajes, ...(movil ? st.mensajesMovil : {}) }} ref={scrollRef}>
        <Mensajes
          mensajes={mensajes} conv={conv} yo={yo} accion={accion} onReenviar={setReenviarMsg} movil={movil}
          onResponder={(m) => { setModo('responder'); setRespondeA(m); requestAnimationFrame(() => textareaRef.current?.focus()) }}
        />
      </div>

      {(!movil || accionesAbiertas) && <div style={st.eventosBar}>
        <button type="button" style={st.linkBtn} onClick={() => setVerEventos((v) => !v)}>{verEventos ? '▾' : '▸'} Historial ({detalle.eventos?.length || 0})</button>
        {verEventos && (
          <div style={st.eventos}>
            {(detalle.eventos || []).map((ev) => (
              <div key={ev.id} style={st.evento}>
                <span style={sx.mono}>{fmtFechaHora(ev.created_at)}</span> · <b>{ev.tipo}</b>{ev.usuario ? ` · ${ev.usuario.nombre}` : ' · sistema'}
                {ev.datos?.motivo ? ` · ${ev.datos.motivo}` : ''}
              </div>
            ))}
          </div>
        )}
      </div>}

      <div style={{ ...st.compositor, ...(movil ? st.compositorMovil : {}) }}>
        <div style={{ ...st.tabs, ...(movil ? { marginBottom: 4 } : {}) }}>
          <button type="button" style={{ ...st.tab, ...(modo === 'responder' ? st.tabOn : {}) }} onClick={() => setModo('responder')}>Responder</button>
          <button type="button" style={{ ...st.tab, ...(modo === 'nota' ? st.tabOn : {}), whiteSpace: 'nowrap' }} onClick={() => setModo('nota')}>Nota interna</button>
          <div style={{ flex: 1 }} />
          {modo === 'responder' && (
            <span style={st.ventana}>
              {spam ? 'En spam: no se responde.'
                : !estado?.outbound ? 'Envíos apagados.'
                : conv.ventana_abierta ? (movil
                    ? <>24 h · vence <span style={sx.mono}>{fmtHoraDe(conv.ventana_vence_at)}</span> {fmtFecha(conv.ventana_vence_at).slice(0, 5)}</>
                    : <>Ventana de 24 h abierta · vence <span style={sx.mono}>{fmtHoraDe(conv.ventana_vence_at)}</span> del {fmtFecha(conv.ventana_vence_at)}</>)
                : (movil ? 'Ventana cerrada: solo plantilla.' : 'Ventana de 24 h cerrada: WhatsApp solo permite una plantilla aprobada (próximamente).')}
            </span>
          )}
        </div>
        {error && <div style={st.error}>{error}</div>}
        {respondeA && modo === 'responder' && (
          <div style={st.citaChip}>
            <div style={{ minWidth: 0 }}>
              <div style={st.citaQuien}>↩ Respondiendo a {respondeA.direccion === 'entrante' ? (nombreDe(conv) || 'el cliente') : 'un mensaje propio'}</div>
              <div style={st.citaTexto}>{previewMensaje(respondeA)}</div>
            </div>
            <button type="button" style={st.linkBtn} onClick={() => setRespondeA(null)}>✕</button>
          </div>
        )}
        {archivo && modo === 'responder' && (
          <div style={st.adjChip}>
            📎 {archivo.name} <span style={{ color: colors.textMeta }}>({Math.max(1, Math.round(archivo.size / 1024))} KB)</span>
            <button type="button" style={st.linkBtn} onClick={() => { setArchivo(null); if (archivoRef.current) archivoRef.current.value = '' }}>✕ Quitar</button>
          </div>
        )}
        {/* Escritorio: cuadro de 3 renglones y barra de botones debajo. Celular:
            una sola fila —emojis, adjuntar, el texto que crece hasta 4 renglones
            y el botón de enviar— como WhatsApp (Martin, 27 sep 2026). */}
        <div style={movil ? st.filaMovil : { display: 'contents' }}>
          {movil && (
            <div style={{ position: 'relative' }}>
              <button type="button" style={st.iconoMovil} title="Emojis" aria-label="Emojis" onClick={() => setEmojis((v) => !v)}>😊</button>
              {emojis && <SelectorEmojis onElegir={insertarEmoji} onCerrar={() => setEmojis(false)} />}
            </div>
          )}
          {movil && modo === 'responder' && (
            <>
              <input ref={archivoRef} type="file" style={{ display: 'none' }} onChange={(e) => setArchivo(e.target.files?.[0] || null)} />
              <button type="button" style={{ ...st.iconoMovil, ...(puedeResponder ? {} : { opacity: 0.55 }) }} title="Adjuntar archivo" aria-label="Adjuntar archivo" disabled={!puedeResponder} onClick={() => archivoRef.current?.click()}>📎</button>
            </>
          )}
          <textarea
            ref={textareaRef}
            style={{ ...st.textarea, ...(modo === 'nota' ? st.textareaNota : {}), ...(movil ? st.textareaMovil : {}) }}
            placeholder={modo === 'nota' ? (movil ? 'Nota interna (el cliente no la ve)' : 'Nota para el equipo — el cliente no la ve. Ej: @logistica ¿podemos Pilar el sábado?') : (movil ? 'Mensaje' : (nombre ? `Escribí un mensaje para ${nombre}…` : 'Escribí un mensaje…'))}
            value={texto}
            onChange={(e) => { setTexto(e.target.value); if (movil) { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 110) + 'px' } }}
            onKeyDown={onKey} rows={movil ? 1 : 3}
            disabled={modo === 'responder' && !puedeResponder}
          />
          {movil && (modo === 'nota'
            ? <button type="button" style={{ ...st.enviarMovil, ...((enviando || !texto.trim()) ? st.enviarMovilOff : {}) }} onClick={enviar} disabled={enviando || !texto.trim()} aria-label="Guardar nota" title="Guardar nota">🔒</button>
            : <button type="button" style={{ ...st.enviarMovil, ...((enviando || (!texto.trim() && !archivo) || !puedeResponder) ? st.enviarMovilOff : {}) }} onClick={enviar} disabled={enviando || (!texto.trim() && !archivo) || !puedeResponder} aria-label="Enviar" title="Enviar">➤</button>)}
        </div>
        {!movil && (
        <div style={st.compositorBar}>
          <div style={{ position: 'relative' }}>
            <button type="button" style={st.emojiBtn} title="Emojis" aria-label="Emojis" onClick={() => setEmojis((v) => !v)}>😊</button>
            {emojis && <SelectorEmojis onElegir={insertarEmoji} onCerrar={() => setEmojis(false)} />}
          </div>
          {modo === 'responder' && (
            <>
              <input
                ref={archivoRef} type="file" style={{ display: 'none' }}
                onChange={(e) => setArchivo(e.target.files?.[0] || null)}
              />
              <button
                type="button" style={st.emojiBtn} title="Adjuntar archivo" aria-label="Adjuntar archivo"
                disabled={!puedeResponder} onClick={() => archivoRef.current?.click()}
              >📎</button>
            </>
          )}
          <span style={st.hint}>Enter envía · Shift+Enter, renglón nuevo</span>
          <div style={{ flex: 1 }} />
          {modo === 'nota'
            ? <Boton variante="primary" onClick={enviar} disabled={enviando || !texto.trim()}>🔒 Guardar nota</Boton>
            : <Boton variante="primary" onClick={enviar} disabled={enviando || (!texto.trim() && !archivo) || !puedeResponder}>{enviando ? 'Enviando…' : 'Enviar'}</Boton>}
        </div>
        )}
      </div>

      {modalAsignar && <ModalAsignar conv={conv} agentes={agentes} equipos={equipos} onClose={() => setModalAsignar(false)} accion={accion} />}
      {modalNombre && <ModalNombre conv={conv} onClose={() => setModalNombre(false)} accion={accion} />}
      {reenviarMsg && <ModalReenviar mensaje={reenviarMsg} onClose={() => setReenviarMsg(null)} />}
    </>
  )
}

// El fragmento de un mensaje para citarlo (chip del compositor y quote de la burbuja).
function previewMensaje(m) {
  if (m?.texto) return m.texto.length > 110 ? m.texto.slice(0, 110) + '…' : m.texto
  return { imagen: '📷 Imagen', video: '🎬 Video', audio: '🎤 Audio', documento: `📄 ${m?.media_nombre || 'Documento'}`, sticker: 'Sticker', ubicacion: '📍 Ubicación', contacto: '👤 Contacto' }[m?.tipo] || 'Mensaje'
}

function Mensajes({ mensajes, conv, yo, accion, onReenviar, onResponder, movil = false }) {
  let diaPrev = null
  const out = []
  for (const m of mensajes) {
    const dia = fmtFecha(m.enviado_at || m.created_at)
    if (dia !== diaPrev) { out.push(<div key={`d${m.id}`} style={st.dia}>{dia === fmtFecha(new Date().toISOString()) ? 'Hoy' : dia}</div>); diaPrev = dia }
    out.push(<Mensaje key={m.id} m={m} conv={conv} yo={yo} accion={accion} onReenviar={onReenviar} onResponder={onResponder} movil={movil} />)
  }
  return out
}

function Mensaje({ m, conv, yo, accion, onReenviar, onResponder, movil = false }) {
  const hora = fmtHoraDe(m.enviado_at || m.created_at)

  if (m.interno) {
    return (
      <div style={st.nota}>
        <div style={st.notaHead}>🔒 Nota interna · {m.remitente_usuario?.nombre || '—'} · <span style={sx.mono}>{hora}</span> · el cliente no la ve</div>
        <div>{m.texto}</div>
      </div>
    )
  }
  // "sistema" que SALIÓ al cliente (cobro automático, derivación, respuesta
  // automática) es un mensaje de WhatsApp como cualquier otro: burbuja de salida
  // (Martin, 2 oct 2026: "lo veo con fondo azul y todo centrado, algo que no es
  // de wa"). El aviso centrado queda solo para lo que no fue al cliente.
  if (m.remitente_tipo === 'sistema' && m.direccion !== 'saliente') {
    return <div style={st.sistema}>{m.texto}</div>
  }
  const automatico = m.remitente_tipo === 'sistema'

  const entrante = m.direccion === 'entrante'
  const celular = m.remitente_tipo === 'app_celular'
  const quien = entrante ? (nombreDe(conv) || 'Cliente')
    : celular ? (m.remitente_usuario ? `📱 App celular · ${m.remitente_usuario.nombre}` : '📱 App celular · sin usuario')
    : automatico ? '🤖 Automático — desde LGR'
    : `${m.remitente_usuario?.nombre || 'LGR'} — desde LGR`

  return (
    <div style={{ ...st.msg, ...(entrante ? st.msgIn : st.msgOut), ...(movil ? { maxWidth: '84%' } : {}) }}>
      <div style={st.quien}>{quien}</div>
      <div style={{ ...st.burbuja, ...(entrante ? st.burbujaIn : celular ? st.burbujaCel : st.burbujaOut), ...(movil ? { fontSize: 15, lineHeight: 1.35 } : {}) }}>
        {m.responde_a && (
          <div style={st.cita}>
            <div style={st.citaQuien}>{m.responde_a.remitente_tipo === 'cliente' ? (nombreDe(conv) || 'Cliente') : 'Ligier'}</div>
            <div style={st.citaTexto}>{previewMensaje(m.responde_a)}</div>
          </div>
        )}
        {m.tipo !== 'texto' && <Adjunto m={m} />}
        {m.texto && <TextoWhatsApp texto={m.texto} conBoton={m.tipo === 'plantilla' || m.direccion === 'saliente'} />}
      </div>
      <div style={{ ...st.meta, ...sx.mono }}>
        {hora}
        {!entrante && <Ticks m={m} />}
        {celular && <span style={{ fontFamily: font.sans }}> · respondido desde el celular</span>}
        {celular && !m.remitente_usuario_id && (
          <button type="button" style={{ ...st.linkBtn, fontFamily: font.sans }} onClick={() => accion(() => whatsappApi.reclamar(m.id))}>¿Fuiste vos? Reclamar</button>
        )}
        <button type="button" style={{ ...st.linkBtn, fontFamily: font.sans, ...(movil ? st.accionMsgMovil : {}) }} title="Responder citando este mensaje" aria-label="Responder citando este mensaje" onClick={() => onResponder?.(m)}>↩{movil ? '' : ' Responder'}</button>
        {(m.tipo === 'texto' ? !!m.texto : !!m.media_path) && (
          <button type="button" style={{ ...st.linkBtn, fontFamily: font.sans, ...(movil ? st.accionMsgMovil : {}) }} title="Reenviar a otra conversación" aria-label="Reenviar a otra conversación" onClick={() => onReenviar?.(m)}>↪{movil ? '' : ' Reenviar'}</button>
        )}
      </div>
    </div>
  )
}

// URLs con esquema, o "www." / dominios conocidos sin él (lo que la gente pega en WhatsApp).
// Sin corchetes: "[Botón → https://…]" se llevaba el "]" y el link de la
// Próxima Selección abría la home (Martin, 8 oct 2026).
const RE_URL = /((?:https?:\/\/|www\.)[^\s<>()[\]]+[^\s<>().,;:!?'"[\]]|(?:[a-z0-9-]+\.)+(?:com|ar|net|org|app|io)(?:\.[a-z]{2})?(?:\/[^\s<>()[\]]*[^\s<>().,;:!?'"[\]])?)/gi

/**
 * El texto como lo ve el cliente en WhatsApp: *negrita*, _cursiva_, ~tachado~
 * y, en un mensaje con botón (plantilla, o uno que mandó LGR en la charla como
 * la Próxima Selección o "Hablar con Ventas"), la última línea "[Botón → link]"
 * (así la guarda LGR) dibujada como el botón que aparece debajo del mensaje.
 */
function TextoWhatsApp({ texto, conBoton }) {
  let cuerpo = String(texto)
  let boton = null
  if (conBoton) {
    const m = cuerpo.match(/\n?\[([^\]\n]+?) → (\S+)\]\s*$/)
    if (m) {
      boton = { texto: m[1], url: m[2] }
      cuerpo = cuerpo.slice(0, m.index)
    }
  }
  return (
    <>
      <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}><TextoConLinks texto={cuerpo} /></div>
      {boton && (
        <a href={boton.url} target="_blank" rel="noreferrer noopener" style={st.botonPlantilla} title={boton.url}>
          ↗ {boton.texto}
        </a>
      )}
    </>
  )
}

const RE_FORMATO = /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/g
const ESTILO_FORMATO = { '*': { fontWeight: 700 }, _: { fontStyle: 'italic' }, '~': { textDecoration: 'line-through' } }

/** *negrita*, _cursiva_ y ~tachado~ de WhatsApp sobre un tramo de texto (sin URLs). */
function conFormato(texto, clave) {
  return String(texto).split(RE_FORMATO).map((parte, i) => {
    const marca = parte.length > 2 ? parte[0] : null
    if (i % 2 === 1 && marca && parte.endsWith(marca) && ESTILO_FORMATO[marca]) {
      return <span key={`${clave}-${i}`} style={ESTILO_FORMATO[marca]}>{parte.slice(1, -1)}</span>
    }
    return parte
  })
}

/** El texto tal cual, con las URLs como links que abren en otra pestaña. */
function TextoConLinks({ texto }) {
  const partes = String(texto).split(RE_URL)
  return partes.map((parte, i) => {
    if (i % 2 === 0) return conFormato(parte, i)
    // "martin@ligier.com.ar": el dominio de un email no es un link.
    if (/[@\w.]$/.test(partes[i - 1] || '')) return parte
    const href = /^https?:\/\//i.test(parte) ? parte : `https://${parte}`
    return <a key={i} href={href} target="_blank" rel="noreferrer noopener" style={{ color: colors.link, textDecoration: 'underline' }}>{parte}</a>
  })
}

// Los que más se usan en una charla de ventas; el teclado del sistema sigue valiendo para el resto.
const EMOJIS = ['😊', '😀', '😂', '😉', '😍', '🥂', '🍷', '🍾', '🥃', '🎁', '👍', '👌', '🙏', '👏', '💪', '🙌', '❤️', '✨', '🎉', '🔥', '✅', '❌', '⏰', '📦', '🚚', '🏬', '📍', '📅', '💳', '💰', '📄', '🧾', '☀️', '🌙', '😅', '🤔', '😢', '🙂', '😎', '🤝']

function SelectorEmojis({ onElegir, onCerrar }) {
  const ref = useRef(null)
  useEffect(() => {
    const fuera = (e) => { if (ref.current && !ref.current.contains(e.target)) onCerrar() }
    const esc = (e) => { if (e.key === 'Escape') onCerrar() }
    document.addEventListener('mousedown', fuera); document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', fuera); document.removeEventListener('keydown', esc) }
  }, [onCerrar])
  return (
    <div ref={ref} style={st.emojiPanel} role="listbox" aria-label="Emojis">
      {EMOJIS.map((e) => <button key={e} type="button" style={st.emojiItem} onClick={() => onElegir(e)}>{e}</button>)}
    </div>
  )
}

function Ticks({ m }) {
  const e = m.estado_proveedor
  // El error de Meta puede ser largo ("Business eligibility payment issue…"): que baje de línea, no que se salga.
  if (e === 'fallido') return <span style={{ color: colors.err, fontFamily: font.sans, whiteSpace: 'normal', overflowWrap: 'anywhere' }} title={m.error || ''}> · ⚠ No se envió{m.error ? `: ${m.error}` : ''}</span>
  if (e === 'leido') return <span style={{ color: colors.link }}> · ✓✓ Leído</span>
  if (e === 'entregado') return <span> · ✓✓ Entregado</span>
  if (e === 'enviado') return <span> · ✓ Enviado</span>
  if (e === 'en_cola') return <span> · ⏳ En cola</span>
  return null
}

/** Adjunto: se baja con el token (un <img src> pelado no lo lleva) y se muestra según el tipo. */
function Adjunto({ m }) {
  const [url, setUrl] = useState(null)
  const [falta, setFalta] = useState(false)
  useEffect(() => {
    let vivo = true, objUrl = null
    if (!m.media_path) { setFalta(true); return undefined }
    whatsappApi.media(m.id).then((r) => {
      objUrl = URL.createObjectURL(r.data)
      if (vivo) setUrl(objUrl)
    }).catch(() => { if (vivo) setFalta(true) })
    return () => { vivo = false; if (objUrl) URL.revokeObjectURL(objUrl) }
  }, [m.id, m.media_path])

  const label = { imagen: '📷 Imagen', video: '🎬 Video', audio: '🎤 Audio', documento: `📄 ${m.media_nombre || 'Documento'}`, sticker: 'Sticker', ubicacion: '📍 Ubicación', contacto: '👤 Contacto', reaccion: 'Reacción' }[m.tipo] || 'Adjunto'

  if (['ubicacion', 'contacto', 'reaccion', 'otro'].includes(m.tipo)) return <div style={st.adjLabel}>{label}</div>
  if (falta) return <div style={st.adjLabel}>{label} <span style={{ color: colors.textMeta }}>(todavía no se descargó)</span></div>
  if (!url) return <div style={st.adjLabel}>{label} …</div>
  if (m.tipo === 'imagen' || m.tipo === 'sticker') return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt="" style={st.imagen} /></a>
  if (m.tipo === 'audio') return <audio controls src={url} style={{ maxWidth: '100%' }} />
  if (m.tipo === 'video') return <video controls src={url} style={st.imagen} />
  return <a href={url} download={m.media_nombre || 'archivo'} style={{ ...st.adjLabel, color: colors.link }}>{label} ↓</a>
}

// ─── Contexto ───────────────────────────────────────────────────────────

function Contexto({ detalle, etiquetas, accion }) {
  const navigate = useNavigate()
  const conv = detalle.conversacion
  const ctx = detalle.contexto || {}
  const cli = ctx.cliente
  const vinculados = ctx.vinculados || []
  const [modalBuscar, setModalBuscar] = useState(false)
  const misEtq = conv.etiquetas || []
  const disponibles = etiquetas.filter((e) => e.activa && !misEtq.some((x) => x.id === e.id))

  return (
    <div style={st.ctx}>
      <div>
        <div style={st.ctxNombre}>{nombreDe(conv) || <span style={{ color: colors.textSec }}>Sin nombre</span>}</div>
        <div style={{ ...sx.mono, fontSize: text.sec, color: colors.textSec }}>{conv.contacto?.telefono_formateado}</div>
        {conv.linea && <div style={{ fontSize: text.sec, color: colors.textMeta, marginTop: 2 }}>Línea: {conv.linea.nombre}</div>}
      </div>

      <div style={st.ctxSec}>
        <div style={sx.cardTitle}>Cliente</div>
        {cli ? (
          <>
            <div style={{ fontWeight: 600, marginTop: 4 }}>{cli.nombre}</div>
            <div style={{ ...sx.mono, fontSize: text.sec, color: colors.textSec }}>{cli.codigo_presea || 'sin nº'} · {cli.cuit || 'sin documento'}</div>
            <Kv k="Localidad" v={[cli.localidad, cli.provincia].filter(Boolean).join(', ') || null} />
            <Kv k="Email" v={cli.email} />
            <Kv k="Teléfono en ficha" v={cli.telefono_wa || cli.telefono} mono />
            <Kv k="Lista" v={cli.lista_precios} mono />
            <Kv k="Vendedor habitual" v={cli.vendedor} />
            {cli.lista_negra && <div style={{ marginTop: 6 }}><Badge bg={colors.errBg} color={colors.err}>Lista negra</Badge></div>}
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              <Boton sm variante="secondary" onClick={() => navigate('/clientes/historia', { state: { cliente: { id: cli.id, nombre: cli.nombre, codigo_presea: cli.codigo_presea, cuit: cli.cuit } } })}>Historia ↗</Boton>
              <Boton sm variante="secondary" onClick={() => setModalBuscar(true)}>Vincular otra ficha…</Boton>
            </div>
            {/* Un número puede ser de varias fichas (sugerencia #67): la lista completa,
                con cuál está EN USO (la que ve el panel y con la que se abren NV). */}
            {vinculados.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: text.sec, color: colors.textSec, marginBottom: 2 }}>
                  {vinculados.length === 1 ? 'Única ficha de este número' : `${vinculados.length} fichas con este número`}
                </div>
                {vinculados.map((c) => (
                  <div key={c.id} style={st.candidato}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: c.en_uso ? 600 : 500, fontSize: text.sec, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.nombre} {c.en_uso && <Badge bg={colors.okBg} color={colors.ok} style={{ marginLeft: 4 }}>En uso</Badge>}
                      </div>
                      <div style={{ ...sx.mono, fontSize: text.meta, color: colors.textMeta }}>{c.codigo_presea || '—'} · {c.cuit || 'sin doc'} · {c.localidad || ''}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      {!c.en_uso && <Boton sm onClick={() => accion(() => whatsappApi.usarCliente(conv.id, c.id))}>Usar</Boton>}
                      <Boton sm variante="ghost" onClick={() => accion(() => whatsappApi.desvincularCliente(conv.id, c.id))} title="Quitar esta ficha del número">Quitar</Boton>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <div style={{ color: colors.textSec, fontSize: text.sec, marginTop: 4 }}>Este teléfono no está vinculado a ninguna ficha.</div>
            {ctx.candidatos?.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: text.sec, color: colors.warn, marginBottom: 4 }}>Fichas con este teléfono — elegí cuál:</div>
                {ctx.candidatos.map((c) => (
                  <div key={c.id} style={st.candidato}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: text.sec, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nombre}</div>
                      <div style={{ ...sx.mono, fontSize: text.meta, color: colors.textMeta }}>{c.codigo_presea || '—'} · {c.cuit || 'sin doc'} · {c.localidad || ''}</div>
                    </div>
                    <Boton sm onClick={() => accion(() => whatsappApi.vincularCliente(conv.id, c.id))}>Vincular</Boton>
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginTop: 8 }}><Boton sm variante="secondary" onClick={() => setModalBuscar(true)}>Buscar ficha…</Boton></div>
          </>
        )}
      </div>

      {cli && ctx.compras && (
        <div style={st.ctxSec}>
          <div style={sx.cardTitle}>Compras</div>
          {/* Grid 2×2 mono tabular (propuesta 2 sep 2026): comparables de un vistazo. */}
          <div style={st.comprasGrid}>
            <div><div style={st.kvK}>Última compra</div><div style={st.kvV}>{ctx.compras.ultima_fecha ? fmtFecha(ctx.compras.ultima_fecha) : '—'}</div></div>
            <div><div style={st.kvK}>12 meses</div><div style={st.kvV}>{ctx.compras.total_12m ? `$${fmtMonto(ctx.compras.total_12m)}` : '$0'}</div></div>
            <div><div style={st.kvK}>Pedidos 12m</div><div style={st.kvV}>{ctx.compras.cantidad_12m || 0}</div></div>
            <div><div style={st.kvK}>Ticket prom.</div><div style={st.kvV}>{ctx.compras.cantidad_12m ? `$${fmtMonto(ctx.compras.total_12m / ctx.compras.cantidad_12m)}` : '—'}</div></div>
          </div>
        </div>
      )}

      {cli && ctx.ultimas_nv?.length > 0 && (
        <div style={st.ctxSec}>
          <div style={sx.cardTitle}>Últimas notas de venta</div>
          {ctx.ultimas_nv.map((nv) => (
            <div key={nv.id} style={st.nvRow}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                <Link to={`/notas-venta/${nv.id}`} style={{ ...sx.mono, color: colors.link, textDecoration: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nv.numero_lgr}</Link>
                <span style={{ ...sx.mono, marginLeft: 'auto', whiteSpace: 'nowrap' }}>{nv.total != null ? `$${fmtMonto(nv.total)}` : '—'}</span>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 2 }}>
                <span style={{ ...sx.mono, color: colors.textSec }}>{fmtFecha(nv.fecha)}</span>
                <Badge>{nv.estado}</Badge>
                {nv.origen && nv.origen !== 'lgr' && <Badge>{nv.origen}</Badge>}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={st.ctxSec}>
        <div style={sx.cardTitle}>Etiquetas</div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
          {misEtq.map((e) => (
            <Badge key={e.id} bg={e.color ? `${e.color}22` : colors.zonaBg} color={e.color || colors.zonaText}>
              {e.nombre} <button type="button" style={st.x} onClick={() => accion(() => whatsappApi.actualizar(conv.id, { etiquetas: misEtq.filter((x) => x.id !== e.id).map((x) => x.id) }))}>✕</button>
            </Badge>
          ))}
          {disponibles.length > 0 && (
            <select style={st.selectSm} value="" onChange={(e) => e.target.value && accion(() => whatsappApi.actualizar(conv.id, { etiquetas: [...misEtq.map((x) => x.id), Number(e.target.value)] }))}>
              <option value="">+ etiqueta</option>
              {disponibles.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
            </select>
          )}
          {misEtq.length === 0 && disponibles.length === 0 && <span style={{ fontSize: text.sec, color: colors.textMeta }}>Sin etiquetas — se crean en Equipos y etiquetas.</span>}
        </div>
      </div>

      {modalBuscar && <ModalBuscarCliente conv={conv} onClose={() => setModalBuscar(false)} accion={accion} />}
    </div>
  )
}

function Kv({ k, v, mono }) {
  return (
    <div style={st.kv}>
      <span style={{ color: colors.textSec, flexShrink: 1 }}>{k}</span>
      <span style={{ marginLeft: 'auto', textAlign: 'right', whiteSpace: 'nowrap', ...(mono ? sx.mono : {}), ...(v == null || v === '' ? { color: colors.textMeta } : {}) }}>{v == null || v === '' ? '—' : v}</span>
    </div>
  )
}

// ─── Modales ────────────────────────────────────────────────────────────

function ModalAsignar({ conv, agentes, equipos, onClose, accion }) {
  const [usuarioId, setUsuarioId] = useState(conv.asignado_usuario_id || '')
  const [equipoId, setEquipoId] = useState(conv.equipo_id || '')
  const [motivo, setMotivo] = useState('')
  const guardar = async () => {
    await accion(() => whatsappApi.asignar(conv.id, { usuario_id: usuarioId || null, equipo_id: equipoId || null, motivo: motivo || null }))
    onClose()
  }
  return (
    <Modal title={conv.asignado ? 'Transferir conversación' : 'Asignar conversación'} onClose={onClose} maxWidth="460px">
      <div style={{ display: 'grid', gap: 12 }}>
        <label style={st.label}>Persona
          <select style={st.input} value={usuarioId} onChange={(e) => setUsuarioId(e.target.value)}>
            <option value="">— Nadie (queda sin asignar) —</option>
            {agentes.map((a) => <option key={a.id} value={a.id}>{a.nombre} · {a.rol}</option>)}
          </select>
        </label>
        <label style={st.label}>Equipo
          <select style={st.input} value={equipoId} onChange={(e) => setEquipoId(e.target.value)}>
            <option value="">— Sin equipo —</option>
            {equipos.filter((e) => e.activo).map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
          </select>
        </label>
        <label style={st.label}>Motivo (queda en el historial)
          <input style={st.input} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej: cliente corporativo, lo atiende Juan" />
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
          <Boton variante="primary" onClick={guardar}>Guardar</Boton>
        </div>
      </div>
    </Modal>
  )
}

function ModalNombre({ conv, onClose, accion }) {
  const [nombre, setNombre] = useState(conv.contacto?.nombre || '')
  const guardar = async () => { await accion(() => whatsappApi.actualizar(conv.id, { contacto_nombre: nombre })); onClose() }
  return (
    <Modal title="Nombre del contacto" onClose={onClose} maxWidth="420px">
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ fontSize: text.sec, color: colors.textSec }}>Cómo lo ve el equipo en la bandeja. En WhatsApp figura como <b>{conv.contacto?.nombre_perfil || '—'}</b>.</div>
        <input style={st.input} value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre" autoFocus />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
          <Boton variante="primary" onClick={guardar}>Guardar</Boton>
        </div>
      </div>
    </Modal>
  )
}

function ModalBuscarCliente({ conv, onClose, accion }) {
  const [q, setQ] = useState('')
  const [res, setRes] = useState([])
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return undefined }
    const t = setTimeout(() => whatsappApi.buscarClientes(q.trim()).then((r) => setRes(r.data)).catch(() => setRes([])), 250)
    return () => clearTimeout(t)
  }, [q])
  return (
    <Modal title="Vincular una ficha de cliente a este número" onClose={onClose} maxWidth="520px">
      <div style={{ display: 'grid', gap: 10 }}>
        <input style={st.input} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre, CUIT o email…" autoFocus />
        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
          {res.map((c) => (
            <div key={c.id} style={st.candidato}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 500, fontSize: text.body }}>{c.nombre}</div>
                <div style={{ ...sx.mono, fontSize: text.meta, color: colors.textMeta }}>{c.codigo_presea || '—'} · {c.cuit || 'sin doc'} · {c.localidad || ''} · {c.telefono_wa || c.telefono || 'sin tel'}</div>
              </div>
              <Boton sm onClick={async () => { await accion(() => whatsappApi.vincularCliente(conv.id, c.id)); onClose() }}>Vincular</Boton>
            </div>
          ))}
          {q.trim().length >= 2 && res.length === 0 && <div style={{ fontSize: text.sec, color: colors.textMeta, padding: 8 }}>Sin resultados.</div>}
        </div>
      </div>
    </Modal>
  )
}

/** Reenviar un mensaje a otra conversación: buscador + click en el destino. */
function ModalReenviar({ mensaje, onClose }) {
  const [q, setQ] = useState('')
  const [convs, setConvs] = useState([])
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState('')
  const [listo, setListo] = useState(null)

  useEffect(() => {
    const t = setTimeout(() => {
      whatsappApi.conversaciones(q.trim() ? { vista: 'todas', q: q.trim() } : { vista: 'todas' })
        .then((r) => setConvs((r.data || []).filter((c) => c.id !== mensaje.conversacion_id)))
        .catch(() => setConvs([]))
    }, 250)
    return () => clearTimeout(t)
  }, [q, mensaje.conversacion_id])

  const mandar = async (destino) => {
    if (enviando) return
    setEnviando(true); setError('')
    try {
      await whatsappApi.reenviar(mensaje.id, destino.id)
      setListo(destino)
    } catch (e) {
      const d = e.response?.data
      setError(d?.errors ? Object.values(d.errors).flat().join(' ') : (d?.message || 'No se pudo reenviar.'))
    } finally { setEnviando(false) }
  }

  const resumen = mensaje.texto
    ? `“${mensaje.texto.length > 90 ? mensaje.texto.slice(0, 90) + '…' : mensaje.texto}”`
    : ({ imagen: '📷 Imagen', video: '🎬 Video', audio: '🎤 Audio', documento: `📄 ${mensaje.media_nombre || 'Documento'}` }[mensaje.tipo] || 'Adjunto')

  return (
    <Modal title="Reenviar mensaje" onClose={onClose} maxWidth="460px">
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ fontSize: text.sec, color: colors.textSec }}>{resumen}</div>
        {listo ? (
          <>
            <div style={{ fontSize: text.body, color: colors.ok }}>✓ Reenviado a {nombreDe(listo) || listo.contacto?.telefono_formateado}.</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}><Boton variante="primary" onClick={onClose}>Listo</Boton></div>
          </>
        ) : (
          <>
            <input
              style={st.input} autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar conversación (nombre o teléfono)…"
            />
            {error && <div style={st.error}>{error}</div>}
            <div style={{ maxHeight: 300, overflowY: 'auto', border: `1px solid ${colors.borderSoft}`, borderRadius: radius.sm }}>
              {convs.length === 0 && <div style={{ padding: 12, fontSize: text.sec, color: colors.textSec }}>Sin conversaciones abiertas que coincidan. Solo se puede reenviar a charlas con la ventana de 24 h abierta.</div>}
              {convs.map((c) => (
                <button
                  key={c.id} type="button" disabled={enviando}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', borderBottom: `1px solid ${colors.borderSoft}`, cursor: 'pointer', fontFamily: 'inherit' }}
                  onClick={() => mandar(c)}
                >
                  <div style={{ fontSize: text.body, color: colors.text }}>{nombreDe(c) || <span style={sx.mono}>{c.contacto?.telefono_formateado}</span>}</div>
                  <div style={{ ...sx.mono, fontSize: text.meta, color: colors.textMeta }}>
                    {c.contacto?.telefono_formateado}{c.ventana_abierta ? '' : ' · ventana de 24 h cerrada'}
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

// ─── Estilos ────────────────────────────────────────────────────────────

// Paleta de WhatsApp (tema claro) — solo para el área del chat.
const WA = { fondo: '#efeae2', entrante: '#ffffff', saliente: '#d9fdd3', sombra: '0 1px 0.5px rgba(11,20,26,.13)' }

const st = {
  page: { display: 'flex', flexDirection: 'column', height: 'calc(100vh - 3rem)', minHeight: 560 },
  // En celular la bandeja tapa el layout de escritorio (menú lateral de 240 px):
  // a pantalla completa, con ← LGR para salir. El resto de LGR no cambia.
  pageMovil: { position: 'fixed', inset: 0, zIndex: 50, height: '100dvh', minHeight: 0, padding: 8, boxSizing: 'border-box', background: colors.bgSoft },
  // Dentro de la app móvil (/m): ocupa el alto entre la cabecera (52) y la barra
  // de pestañas (58 + safe area) del MovilLayout, sin taparlos.
  pageMovilApp: { height: 'calc(100dvh - 52px - 58px - env(safe-area-inset-bottom))', minHeight: 0, padding: 0, boxSizing: 'border-box', background: colors.bg },
  shellMovil: { border: 'none', borderRadius: 0 },
  salir: { color: colors.link, textDecoration: 'none', fontSize: text.body, fontWeight: 500, marginRight: 12 },
  colMovil: { width: '100%', minWidth: 0, borderLeft: 'none', borderRight: 'none' },
  volverBar: { padding: '8px 12px', borderBottom: `1px solid ${colors.border}` },
  volver: { background: 'none', border: 'none', fontSize: 20, lineHeight: 1, padding: '2px 6px 2px 0', cursor: 'pointer', color: colors.link, fontFamily: 'inherit' },
  // "⋯" del celular: es un toggle, no un link — gris, azul solo cuando está abierto; caja de 40×44 para el pulgar.
  puntos: { background: 'none', border: 'none', fontSize: 22, lineHeight: 1, width: 40, height: 44, cursor: 'pointer', color: colors.textSec, fontFamily: 'inherit', flexShrink: 0 },
  avatar: { width: 34, height: 34, borderRadius: '50%', background: colors.bgSoft, border: `1px solid ${colors.border}`, color: colors.text, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, flexShrink: 0 },
  head: { display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 10, minWidth: 0 },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text, whiteSpace: 'nowrap' },
  // Stats de cabecera en texto plano (fuera las tarjetas KPI — 2 sep 2026).
  stats: { marginLeft: 'auto', display: 'flex', gap: 18, alignItems: 'baseline', flexShrink: 0 },
  stat: { fontSize: text.sec, color: colors.textSec, whiteSpace: 'nowrap' },
  statV: { ...sx.mono, fontSize: text.body, fontWeight: 600, color: colors.text },
  statDot: { display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: colors.warnStrong, marginRight: 5, verticalAlign: 'middle' },
  bannerWarn: { background: colors.warnBg, border: `1px solid ${colors.warnBorder}`, color: colors.warn, borderRadius: radius.md, padding: '6px 12px', fontSize: text.sec, marginBottom: 10 },
  bannerWarnSlim: { background: colors.warnBg, color: colors.warn, padding: '6px 16px', fontSize: text.sec, borderBottom: `1px solid ${colors.border}` },
  shell: { flex: 1, minHeight: 0, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.lg, display: 'flex', overflow: 'hidden' },

  left: { width: 320, minWidth: 320, borderRight: `1px solid ${colors.border}`, display: 'flex', flexDirection: 'column', minHeight: 0 },
  search: { ...sx.input, margin: '10px 10px 8px', minWidth: 0, textTransform: 'none' },
  // Chips de vista en UNA sola fila (las 4 de trabajo + "⋯" con el archivo).
  chips: { display: 'flex', gap: 3, padding: '0 10px 8px', alignItems: 'center' },
  chip: { ...sx.pill, padding: '3px 8px', fontSize: text.meta, lineHeight: '15px', whiteSpace: 'nowrap', fontFamily: 'inherit', cursor: 'pointer' },
  chipOn: { ...sx.pillActive },
  chipN: { fontWeight: 600, color: colors.textMeta, marginLeft: 4, fontVariantNumeric: 'tabular-nums' },
  filtroRow: { display: 'flex', gap: 4, padding: '0 10px 8px', alignItems: 'center', borderBottom: `1px solid ${colors.borderSoft}` },
  filtroActivo: { border: 'none', background: colors.bgSoft, color: colors.textSub, borderRadius: radius.pill, padding: '3px 10px', fontSize: text.meta, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  archivoPop: { position: 'absolute', top: '110%', right: 0, zIndex: 20, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.dropdown, padding: 4, minWidth: 130 },
  archivoItem: { display: 'flex', justifyContent: 'space-between', gap: 10, width: '100%', border: 'none', background: 'none', textAlign: 'left', padding: '6px 10px', fontSize: text.sec, color: colors.textSub, cursor: 'pointer', borderRadius: radius.sm, fontFamily: 'inherit' },
  selectSm: { textTransform: 'none', flex: 1, minWidth: 0, border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: '3px 6px', fontSize: text.meta, color: colors.textSub, background: colors.bg, fontFamily: 'inherit' },
  listaScroll: { flex: 1, overflowY: 'auto', minHeight: 0 },
  vacio: { padding: 24, textAlign: 'center', color: colors.textMeta, fontSize: text.sec },
  grupo: { padding: '5px 14px', fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', background: colors.bgSoft, borderBottom: `1px solid ${colors.borderSoft}` },
  // Fila de 2 líneas exactas: nombre+hora / preview+señal (2 sep 2026).
  fila: { padding: '9px 14px', borderBottom: `1px solid ${colors.borderSoft}`, display: 'flex', flexDirection: 'column', gap: 2, cursor: 'pointer' },
  filaSel: { background: colors.linkBg, boxShadow: `inset 3px 0 0 ${colors.link}` },
  filaTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 },
  filaNombre: { fontWeight: 600, fontSize: text.body, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  filaHora: { fontSize: text.meta, color: colors.textMeta, flexShrink: 0 },
  filaPreview: { color: colors.textSec, fontSize: text.sec, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 },
  // El verde de WhatsApp solo señala MENSAJES (pill de no leídos); todo lo demás es slate/ámbar.
  noLeidos: { flexShrink: 0, background: '#25d366', color: colors.bg, borderRadius: radius.pill, fontSize: text.meta, fontWeight: 700, minWidth: 17, height: 17, padding: '0 5px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontVariantNumeric: 'tabular-nums' },
  sinAsignar: { flexShrink: 0, fontSize: text.meta, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: colors.warn, background: colors.warnBg, borderRadius: radius.sm, padding: '2px 6px' },

  center: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 },
  vacioCentro: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.textMeta, background: colors.bgSoft },
  chatHead: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderBottom: `1px solid ${colors.border}`, flexWrap: 'wrap' },
  chatHeadMovil: { padding: '8px 10px', gap: 6 },
  // Celular: las acciones se abren con "⋯" y ocupan una fila propia debajo del nombre.
  accionesMovil: { display: 'flex', flexWrap: 'wrap', gap: 8, width: '100%', paddingTop: 8 },
  selectsMovil: { display: 'flex', gap: 8, width: '100%', paddingTop: 8 },
  selectMovil: { flex: 1, height: 40, padding: '4px 8px', borderRadius: radius.sm },
  chatNombre: { fontSize: text.title, fontWeight: 600, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 },
  chatSub: { color: colors.textSec, fontSize: text.sec },
  lapiz: { border: 'none', background: 'none', cursor: 'pointer', color: colors.textMeta, fontSize: 12, padding: 0 },
  selectInline: { textTransform: 'none', border: `1px solid ${colors.border}`, borderRadius: radius.xs, padding: '2px 4px', fontSize: text.meta, color: colors.textSub, background: colors.bg, fontFamily: 'inherit' },
  // Colores de WhatsApp (pedido de Martin 27 ago 2026: "colores iguales"): fondo del chat,
  // burbuja blanca del cliente, verde de lo que sale de Ligier. El resto sigue con los tokens de LGR.
  mensajes: { flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '12px 20px', background: WA.fondo, display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0 },
  dia: { alignSelf: 'center', fontSize: text.meta, color: colors.textSec, background: '#fff', border: 'none', boxShadow: WA.sombra, borderRadius: radius.pill, padding: '2px 10px' },
  // maxWidth en % y overflowWrap: un link largo (wa.me/…?text=…) hacía que el aviso midiera 616px en un celular de 390 (28 sep 2026).
  sistema: { alignSelf: 'center', fontSize: text.meta, color: colors.textSec, background: colors.borderSoft, borderRadius: radius.sm, padding: '4px 10px', maxWidth: 'min(640px, 100%)', textAlign: 'center', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' },
  msg: { maxWidth: '72%', display: 'flex', flexDirection: 'column', gap: 3 },
  msgIn: { alignSelf: 'flex-start' },
  msgOut: { alignSelf: 'flex-end', alignItems: 'flex-end' },
  quien: { fontSize: text.meta, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: colors.textSec },
  // maxWidth 100% + minWidth 0: la burbuja es hija de un flex con alignItems flex-end, así que sin tope
  // toma el ancho de su contenido más largo (la cita en una línea) y se sale de la pantalla por la
  // izquierda — Martin, 28 sep 2026: "hay mensajes de wa que siguen cortados… en mobile".
  burbuja: { background: WA.entrante, border: 'none', boxShadow: WA.sombra, borderRadius: radius.lg, padding: '7px 11px', fontSize: text.body, lineHeight: 1.4, color: colors.text, maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' },
  burbujaIn: { borderTopLeftRadius: 3 },
  burbujaOut: { background: WA.saliente, borderTopRightRadius: 3 },
  // El botón URL de una plantilla, como lo dibuja WhatsApp: separado por una línea, centrado, en el color del link.
  botonPlantilla: { display: 'block', marginTop: 6, paddingTop: 6, borderTop: `1px solid ${colors.borderSoft}`, textAlign: 'center', color: colors.link, fontWeight: 500, textDecoration: 'none' },
  burbujaCel: { background: WA.saliente, borderTopRightRadius: 3 },
  meta: { fontSize: text.meta, color: colors.textMeta, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', whiteSpace: 'nowrap', maxWidth: '100%' },
  nota: { alignSelf: 'stretch', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: radius.md, padding: '8px 12px', fontSize: text.sec, color: colors.warn, lineHeight: 1.45, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', minWidth: 0 },
  notaHead: { fontSize: text.meta, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 3, color: colors.warn },
  adjLabel: { fontSize: text.sec, color: colors.textSec, marginBottom: 4 },
  adjChip: { display: 'flex', alignItems: 'center', gap: 8, fontSize: text.sec, color: colors.text, background: colors.bgSoft, border: `1px solid ${colors.borderSoft}`, borderRadius: radius.sm, padding: '4px 10px', marginTop: 6 },
  // Cita (responder a un mensaje): barrita verde + fragmento, como WhatsApp.
  cita: { borderLeft: '3px solid #25d366', background: 'rgba(11,20,26,.05)', borderRadius: 6, padding: '4px 8px', marginBottom: 6, minWidth: 0, overflow: 'hidden' },
  citaChip: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, borderLeft: '3px solid #25d366', background: colors.bgSoft, borderRadius: radius.sm, padding: '6px 10px', marginTop: 6 },
  citaQuien: { fontSize: text.meta, fontWeight: 600, color: '#128c7e' },
  citaTexto: { fontSize: text.sec, color: colors.textSec, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  imagen: { maxWidth: 'min(280px, 100%)', maxHeight: 280, borderRadius: radius.sm, display: 'block', marginBottom: 4 },
  eventosBar: { borderTop: `1px solid ${colors.borderSoft}`, padding: '4px 16px', fontSize: text.meta },
  eventos: { maxHeight: 120, overflowY: 'auto', paddingTop: 4 },
  evento: { fontSize: text.meta, color: colors.textSec, padding: '2px 0' },
  linkBtn: { border: 'none', background: 'none', color: colors.link, cursor: 'pointer', padding: 0, fontSize: text.meta, fontFamily: 'inherit', whiteSpace: 'nowrap' },
  compositor: { borderTop: `1px solid ${colors.border}`, padding: '8px 16px 12px' },
  // Celular: compositor de una fila, pegado abajo, con el texto que crece (máx. 4 renglones).
  compositorMovil: { padding: '6px 8px 8px' },
  filaMovil: { display: 'flex', alignItems: 'flex-end', gap: 6 },
  textareaMovil: { flex: 1, minWidth: 0, resize: 'none', borderRadius: 18, padding: '8px 12px', lineHeight: 1.3, maxHeight: 110, overflowY: 'auto' },
  enviarMovil: { border: 'none', background: colors.primary, color: '#fff', width: 44, height: 44, borderRadius: radius.pill, fontSize: 18, lineHeight: 1, cursor: 'pointer', flexShrink: 0 },
  enviarMovilOff: { opacity: 0.55, cursor: 'default' },
  // 😊 y 📎 en el celular: sin caja, 40×40, gris (misma receta que iconBtn del layout móvil)
  iconoMovil: { border: 'none', background: 'none', fontSize: 22, lineHeight: 1, width: 40, height: 40, padding: 0, cursor: 'pointer', color: colors.textSec, flexShrink: 0 },
  accionMsgMovil: { minWidth: 32, minHeight: 28, fontSize: 15, color: colors.textSec },
  mensajesMovil: { padding: '8px 10px' },
  tabs: { display: 'flex', gap: 14, alignItems: 'center', marginBottom: 6 },
  tab: { border: 'none', background: 'none', color: colors.textMeta, fontSize: text.sec, cursor: 'pointer', padding: '0 0 4px', fontFamily: 'inherit', fontWeight: 500 },
  tabOn: { color: colors.link, fontWeight: 600, borderBottom: `2px solid ${colors.link}` },
  ventana: { fontSize: text.meta, color: colors.textMeta, textAlign: 'right' },
  textarea: { textTransform: 'none', width: '100%', border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, padding: '8px 10px', fontSize: text.body, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' },
  textareaNota: { background: '#fffbeb', borderColor: '#fde68a' },
  compositorBar: { display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' },
  emojiBtn: { border: `1px solid ${colors.border}`, background: colors.bg, borderRadius: radius.sm, fontSize: 16, lineHeight: 1, padding: '4px 7px', cursor: 'pointer', height: 32, flexShrink: 0 },
  emojiPanel: { position: 'absolute', bottom: '110%', left: 0, zIndex: 20, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: '0 8px 24px rgba(0,0,0,.12)', padding: 6, display: 'grid', gridTemplateColumns: 'repeat(8, 32px)', gap: 2 },
  emojiItem: { border: 'none', background: 'none', fontSize: 20, lineHeight: 1, width: 32, height: 32, borderRadius: radius.sm, cursor: 'pointer', padding: 0 },
  hint: { fontSize: text.meta, color: colors.textPlh },
  error: { background: colors.errBg, color: colors.err, borderRadius: radius.sm, padding: '6px 10px', fontSize: text.sec, marginTop: 6 },

  right: { width: 290, minWidth: 290, borderLeft: `1px solid ${colors.border}`, overflowY: 'auto', minHeight: 0 },
  ctx: { padding: '14px 14px', display: 'flex', flexDirection: 'column', gap: 14 },
  ctxSec: { borderTop: `1px solid ${colors.borderSoft}`, paddingTop: 12 },
  comprasGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px', marginTop: 6 },
  kvK: { fontSize: text.meta, color: colors.textMeta },
  kvV: { ...sx.mono, fontSize: text.body },
  ctxNombre: { fontSize: text.title, fontWeight: 600 },
  kv: { display: 'flex', gap: 6, fontSize: text.sec, padding: '3px 0', borderBottom: `1px solid ${colors.borderSoft}`, alignItems: 'center' },
  nvRow: { fontSize: text.sec, padding: '4px 0', borderBottom: `1px solid ${colors.borderSoft}` },
  candidato: { display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${colors.borderSoft}` },
  x: { border: 'none', background: 'none', cursor: 'pointer', color: 'inherit', padding: '0 0 0 2px', fontSize: text.meta },
  label: { display: 'grid', gap: 4, fontSize: text.sec, color: colors.textSec },
  input: { ...sx.input, width: '100%', boxSizing: 'border-box', textTransform: 'none' },
}
